<script setup>
import { computed } from 'vue'
import { ChevronDown } from 'lucide-vue-next'

// Assumes standard shadcn-vue file structure.
// Adjust paths if your components are located elsewhere.
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * UmisisDropdownMenu
 * A wrapper around shadcn-vue DropdownMenu to mimic bootstrap-vue API.
 */
const props = defineProps({
  // The text label for the button
  text: {
    type: String,
    default: '',
  },
  // Button variant: 'primary', 'secondary', 'danger', 'outline', 'ghost', 'link'
  // Mapped to shadcn variants below
  variant: {
    type: String,
    default: 'default',
  },
  // Button size: 'default', 'sm', 'lg', 'icon'
  size: {
    type: String,
    default: 'default',
  },
  // If true, creates a split button (left action, right toggle)
  split: {
    type: Boolean,
    default: false,
  },
  // Align menu to the right (end)
  right: {
    type: Boolean,
    default: false,
  },
  // Hide the chevron icon (not applicable in split mode)
  noCaret: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Pass-through classes
  class: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['click'])

// Map Bootstrap-Vue terminology to Shadcn-Vue variants
const computedVariant = computed(() => {
  const v = props.variant

  switch (v) {
  case 'primary': return 'default'
  case 'danger': return 'destructive'
  case 'light': return 'secondary' // approximate mapping
  case 'success': return 'default' // shadcn usually lacks success, defaulting to primary
  case 'info': return 'secondary'
    // Pass through standard shadcn variants (outline, ghost, link, secondary)
  default: return v
  }
})

// Map 'right' prop to Radix align property
const align = computed(() => props.right ? 'end' : 'start')
</script>

<template>
  <!--
    SPLIT BUTTON MODE
    Renders two buttons: One for the action, one for the menu toggle.
  -->
  <div v-if="split" :class="['inline-flex items-center whitespace-nowrap', props.class]">
    <!-- Action Button -->
    <Button
      :variant="computedVariant"
      :size="size"
      :disabled="disabled"
      class="rounded-r-none border-r-0 focus:z-10"
      @click="emit('click', $event)"
    >
      <slot name="button-content">
        {{ text }}
      </slot>
    </Button>

    <!-- Toggle Button (Chevron) -->
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button
          :variant="computedVariant"
          :size="size"
          :disabled="disabled"
          class="rounded-l-none px-2 ml-[1px] focus:z-10"
        >
          <ChevronDown class="h-4 w-4" />
          <span class="sr-only">Toggle Dropdown</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent :align="align" class="w-56">
        <!-- Default slot for DropdownMenuItems -->
        <slot />
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <!--
    STANDARD DROPDOWN MODE
    Renders a single button that toggles the menu.
  -->
  <DropdownMenu v-else>
    <DropdownMenuTrigger as-child>
      <Button :variant="computedVariant" :size="size" :disabled="disabled" :class="['gap-2', props.class]">
        <slot name="button-content">
          {{ text }}
        </slot>
        <ChevronDown v-if="!noCaret" class="h-4 w-4 opacity-50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </Button>
    </DropdownMenuTrigger>

    <DropdownMenuContent :align="align" class="w-56">
      <!-- Default slot for DropdownMenuItems -->
      <slot />
    </DropdownMenuContent>
  </DropdownMenu>
</template>
